import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronRight, Box, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import client, { apiErr } from "@/lib/api";
import { fmt } from "@/lib/catalog";

const PAY_LABEL = { cod: "دفع عند الاستلام", online: "دفع إلكتروني", wallet: "المحفظة" };
const PAY_STATUS = { paid: ["مدفوع", "var(--k-emerald)", "var(--k-emerald-tint)"], pending: ["بانتظار الدفع", "var(--k-gold-deep)", "var(--k-gold-tint)"], cod_pending: ["عند الاستلام", "var(--k-ink-soft)", "var(--k-surface)"], awaiting_transfer: ["بانتظار التحويل", "var(--k-gold-deep)", "var(--k-gold-tint)"] };
const FULFILL = {
  pending: ["قيد الانتظار", "var(--k-ink-soft)"], payment_confirmed: ["تم تأكيد الدفع", "var(--k-emerald)"],
  confirmed: ["مؤكّد", "var(--k-emerald)"], processing: ["قيد التجهيز", "var(--k-gold-deep)"],
  shipped: ["قيد الشحن", "var(--k-gold-deep)"], delivered: ["تم التسليم", "var(--k-emerald)"],
  cancelled: ["ملغي", "var(--k-ruby)"], disputed: ["نزاع", "var(--k-ruby)"],
};

export default function Orders() {
  const nav = useNavigate();
  const [orders, setOrders] = useState(null);

  const load = () => client.get("/orders/mine").then((r) => setOrders(r.data)).catch(() => nav("/auth?next=/orders"));
  useEffect(() => { load(); }, []);

  const openDispute = async (orderId) => {
    const reason = window.prompt("اذكر سبب النزاع على هذا الطلب:");
    if (!reason || !reason.trim()) return;
    try { await client.post(`/orders/${orderId}/dispute`, { reason: reason.trim() }); toast.success("تم فتح النزاع، ستتواصل معك الإدارة"); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  return (
    <div dir="rtl">
      <div className="sticky top-0 z-20 flex items-center gap-2.5 px-3 py-3" style={{ background: "var(--k-paper)", borderBottom: "1px solid var(--k-line)" }}>
        <button data-testid="back-btn" onClick={() => nav(-1)} className="k-tap w-10 h-10 rounded-full flex items-center justify-center" style={{ background: "var(--k-surface)", border: "1px solid var(--k-line)" }}><ChevronRight size={20} /></button>
        <h1 className="font-head text-[17px] font-bold">طلباتي</h1>
      </div>

      {orders === null ? (
        <div className="p-6 text-center" style={{ color: "var(--k-ink-soft)" }}>جارٍ التحميل…</div>
      ) : orders.length === 0 ? (
        <div className="text-center pt-20 px-8" style={{ color: "var(--k-ink-soft)" }}>
          <Box size={44} className="mx-auto mb-3 opacity-50" />
          <p className="text-[13.5px]">لا توجد طلبات بعد</p>
        </div>
      ) : (
        <div className="pt-3 px-4 grid grid-cols-1 md:grid-cols-2 gap-3">
          {orders.map((o) => {
            const [payLbl, payCol, payBg] = PAY_STATUS[o.payStatus] || PAY_STATUS.cod_pending;
            const [fLbl, fCol] = FULFILL[o.status || "pending"] || FULFILL.pending;
            const canDispute = !["cancelled", "disputed", "delivered"].includes(o.status);
            return (
              <div key={o.orderId} data-testid={`order-${o.orderId}`} className="rounded-2xl p-4" style={{ background: "var(--k-surface)", border: "1px solid var(--k-line)" }}>
                <div className="flex justify-between items-center mb-2 gap-2 flex-wrap">
                  <span className="font-num font-bold text-[13px]">{o.orderId}</span>
                  <div className="flex gap-1.5">
                    <span className="text-[10.5px] font-bold px-2.5 py-1 rounded-full" style={{ color: fCol, background: "var(--k-paper)", border: "1px solid var(--k-line)" }}>{fLbl}</span>
                    <span className="text-[10.5px] font-bold px-2.5 py-1 rounded-full" style={{ color: payCol, background: payBg, border: "1px solid var(--k-line)" }}>{payLbl}</span>
                  </div>
                </div>
                <p className="text-[11.5px] mb-2" style={{ color: "var(--k-ink-soft)" }}>{o.items.length} صنف · {PAY_LABEL[o.payMethod] || o.payMethodLabel} · التوصيل: {o.destinationCity || o.city}</p>
                {o.items.map((i, k) => (
                  <div key={k} className="flex justify-between text-[12px] py-0.5" style={{ color: "var(--k-ink-soft)" }}>
                    <span>{i.name} × {i.qty}</span><span className="font-num">{fmt(i.subtotal)}</span>
                  </div>
                ))}
                <div className="flex justify-between text-[13.5px] font-bold pt-2 mt-1.5" style={{ borderTop: "1px solid var(--k-line)" }}>
                  <span>الإجمالي</span><span className="font-num">{fmt(o.total)} د.ل</span>
                </div>

                {Array.isArray(o.timeline) && o.timeline.length > 0 && (
                  <div className="mt-3 pt-3" style={{ borderTop: "1px dashed var(--k-line)" }}>
                    <p className="text-[11px] font-bold mb-1.5" style={{ color: "var(--k-ink-soft)" }}>مسار الطلب</p>
                    {o.timeline.map((t, k) => (
                      <div key={k} className="flex items-center gap-2 text-[11px] py-0.5" style={{ color: "var(--k-ink-soft)" }}>
                        <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: "var(--k-gold)" }} />
                        <span className="font-bold" style={{ color: "var(--k-ink)" }}>{FULFILL[t.status]?.[0] || t.status}</span>
                        <span className="font-num text-[10px]">{(t.at || "").slice(0, 16).replace("T", " ")}</span>
                      </div>
                    ))}
                  </div>
                )}

                {canDispute && (
                  <button data-testid={`dispute-${o.orderId}`} onClick={() => openDispute(o.orderId)} className="k-tap w-full mt-3 py-2 rounded-lg text-[11.5px] font-bold flex items-center justify-center gap-1.5" style={{ background: "var(--k-ruby-tint)", color: "var(--k-ruby)" }}>
                    <AlertTriangle size={13} /> فتح نزاع على الطلب
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
